import { AuthenticatedGuard } from "@/components/authenticated-guard";
import { GovernmentOperatorImpersonationGate } from "@/components/government/operator-impersonation-gate";
import Header from "@/components/header.tsx";
import { PortalSubNav } from "@/components/portal/portal-sub-nav";
import { UserType } from "@/constants";
import { useLicenseChangeEvents } from "@/hooks/use-license-change-events";
import { isOperatorGovernmentImpersonating } from "@/lib/government-impersonate";
import { getGovernmentLoginConnection } from "@/lib/government-login-connection";
import { isPortalSetupPath } from "@/lib/portal-nav";
import { getUserType } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { Outlet, createFileRoute, useLocation } from "@tanstack/react-router";
import Auth0Provider from "../integrations/auth0/root-provider.tsx";

function GovernmentLayout() {
  const { user } = useAuth0();
  const userType = getUserType(user);
  const { pathname } = useLocation();
  useLicenseChangeEvents();
  const impersonating =
    userType === UserType.OPERATOR && isOperatorGovernmentImpersonating();
  const showPortalSubNav =
    (userType === UserType.GOVERNMENT || impersonating) &&
    !isPortalSetupPath(pathname);

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <Header
        user={user || null}
        fullWidth={true}
        showGovernmentOperatorImpersonation
        showGovernmentHelp
        blogPath="/government/blog"
      />
      {showPortalSubNav && <PortalSubNav userType={UserType.GOVERNMENT} />}
      <main className="flex flex-1 min-h-0 w-full overflow-hidden p-4 md:p-6 bg-background">
        <AuthenticatedGuard
          allowedUserTypes={[UserType.GOVERNMENT, UserType.OPERATOR]}
          loginConnection={getGovernmentLoginConnection()}
        >
          <GovernmentOperatorImpersonationGate>
            <div
              data-slot="portal-page-scroll"
              className="flex flex-1 min-h-0 w-full min-w-0 flex-col overflow-y-auto"
            >
              <Outlet />
            </div>
          </GovernmentOperatorImpersonationGate>
        </AuthenticatedGuard>
      </main>
    </div>
  );
}

export const Route = createFileRoute("/government")({
  component: () => (
    <Auth0Provider
      audience={import.meta.env.VITE_AUTH0_GOVERNMENT_AUDIENCE}
      clientId={import.meta.env.VITE_AUTH0_GOVERNMENT_CLIENT_ID}
      callbackPath="/government/auth_callback"
    >
      <GovernmentLayout />
    </Auth0Provider>
  ),
});
